import storageService, { type ThemePreference } from '../utils/localStorage';
import userService from '../services/user';
import type { AppDispatch } from '../store';
import type { ReduxAction } from '../types/common';

export type { ThemePreference };

type ThemeState = ThemePreference;

const themeReducer = (state: ThemeState = 'system', action: ReduxAction<ThemePreference>): ThemeState => {
  switch (action.type) {
    case 'SET_THEME_PREFERENCE':
      return action.payload ?? state;
    default:
      return state;
  }
};

// Apply a preference locally without hitting the server. Used when applying the
// account's saved preference right after login.
export const applyThemePreference = (pref: ThemePreference) => {
  return (dispatch: AppDispatch) => {
    storageService.saveThemePreference(pref);
    dispatch({ type: 'SET_THEME_PREFERENCE', payload: pref });
  };
};

// User-initiated change: persist on-device and, if signed in, sync to the account
// so the preference follows the user across web and mobile.
export const setThemePreference = (pref: ThemePreference) => {
  return async (dispatch: AppDispatch) => {
    await storageService.saveThemePreference(pref);
    dispatch({ type: 'SET_THEME_PREFERENCE', payload: pref });

    const loggedUser = storageService.loadUser();
    if (loggedUser?.token) {
      try {
        await userService.updateThemePreference(pref);
        storageService.saveUser({ ...loggedUser, themePreference: pref });
      } catch (err) {
        // Local preference still applies; server will reconcile on next change/login.
      }
    }
  };
};

// Resolve the initial preference on boot: a signed-in account's saved value wins,
// otherwise the device-local value (defaulting to 'system').
export const initThemePreference = () => {
  return async (dispatch: AppDispatch) => {
    const local = await storageService.loadThemePreference();
    const loggedUser = storageService.loadUser();
    const accountPref = loggedUser?.themePreference as ThemePreference | undefined;
    const pref: ThemePreference =
      accountPref === 'light' || accountPref === 'dark' || accountPref === 'system'
        ? accountPref
        : local;

    dispatch({ type: 'SET_THEME_PREFERENCE', payload: pref });
  };
};

export default themeReducer;
